import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DiscontinuationRateInputComponent } from '@widgets/discontinuation-rate-input/discontinuation-rate-input.component';

import { PatientGroupDropdownComponent } from '@features/patient-group-dropdown/patient-group-dropdown.component';
import {
  FilterCostOption,
  SiteBudgetCostTypeDropdownComponent,
} from '@features/site-budget-cost-type-dropdown/site-budget-cost-type-dropdown.component';

import { SiteCostType } from '@models/sites/site-cost.enum';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ButtonToggleItem } from '@shared/components/button-toggle-group/button-toggle-item.model';
import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { EditableListDropdownComponent } from '@shared/components/editable-list-dropdown/editable-list-dropdown.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { WorkflowStep } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Option } from '@shared/types/components.type';
import { Maybe } from '@shared/utils/utils';

export interface ProtocolForm {
  patientGroup: null | string;
  protocolVersion: null | string;
  siteBudgetCostType: SiteCostType;
}

type VersionOption = Option & { effective_date?: Maybe<string> };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectModule,
    ReactiveFormsModule,
    NgClass,
    NgTemplateOutlet,
    TooltipDirective,
    SiteBudgetCostTypeDropdownComponent,
    PatientGroupDropdownComponent,
    EditableListDropdownComponent,
    ButtonComponent,
    DiscontinuationRateInputComponent,
  ],
  selector: 'aux-protocol-section',
  styleUrls: ['./protocol-section.component.scss'],
  templateUrl: './protocol-section.component.html',
})
export class ProtocolSectionComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private overlayService = inject(OverlayService);
  private workflowQuery = inject(WorkflowQuery);

  enhancedVersionList = signal<EditableListDropdownItem[]>([]);
  selectedCostType = signal<SiteCostType>(SiteCostType.PATIENT_VISITS);

  isVisitCostSelected = computed(() => {
    return this.selectedCostType() === SiteCostType.PATIENT_VISITS;
  });

  userHasUpdateProtocolEntryPermission = input(false);

  noPermissionTooltip = computed(() =>
    this.userHasUpdateProtocolEntryPermission()
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
  );

  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  patientTrackerLockedTooltip = computed(() =>
    this.isPatientsFinalized() ? MessagesConstants.PATIENT_TRACKER_CLOSED : ''
  );

  editMode = input(false);
  filterCostOption = input<FilterCostOption>();
  hasChanges = input(false);
  showDiscontinuationRate = input(false);

  discontinuationRateInput = viewChild<DiscontinuationRateInputComponent>(
    DiscontinuationRateInputComponent
  );

  @Input() alwaysShowSection = false;
  @Output() amendmentClick = new EventEmitter<boolean>();
  @Input() className = '';
  @Output() deleteProtocolVersion = new EventEmitter<EditableListDropdownItem>();
  @Output() editProtocolVersion = new EventEmitter<EditableListDropdownItem>();
  form = this.fb.group<ProtocolForm>({
    patientGroup: null,
    protocolVersion: null,
    siteBudgetCostType: SiteCostType.PATIENT_VISITS,
  });
  @Output() formReady = new EventEmitter<FormGroup>();
  @Input() hideAmendment = false;
  @Input() isAmendmentDisabled = false;
  @Input() isVersionControlEditable = false;
  @Input() labelDirection: 'horizontal' | 'vertical' = 'horizontal';
  @Input() patientOptions!: ButtonToggleItem[];
  siteCostType = SiteCostType;
  @Input() template?: TemplateRef<unknown>;
  @Output() valuesChange = new EventEmitter();

  private _versionOptions!: VersionOption[];

  ngOnInit(): void {
    let previousFormValue = this.form.value;

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (formValues) => {
        const canDeactivate = await this.canDeactivate();

        if (!canDeactivate) {
          this.form.patchValue(previousFormValue, { emitEvent: false });

          return;
        }

        previousFormValue = formValues;
        this.selectedCostType.set(formValues.siteBudgetCostType ?? SiteCostType.PATIENT_VISITS);
        this.valuesChange.emit(formValues);
      });

    this.formReady.emit(this.form);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  constructor() {
    effect(() => {
      this.enhancedVersionList.set(this.convertOptionToEditableState(this.versionOptions));
    });

    effect(() => {
      const patientGroupControl = this.form.controls.patientGroup;

      if (this.isVisitCostSelected()) {
        patientGroupControl.enable();
        patientGroupControl.setValue(
          this.patientOptions.length ? this.patientOptions[0].value : null
        );
      } else {
        patientGroupControl.setValue(' ');
        patientGroupControl.disable();
      }
    });
  }

  convertOptionToEditableState(
    versionOptions: typeof this.versionOptions
  ): EditableListDropdownItem[] {
    return versionOptions.map((item, i, arr) => ({
      bottomText: `Effective Date: ${item.effective_date || ''}`,
      disabled: this.isPatientsFinalized(),
      isDeletable: i !== 0 && this.userHasUpdateProtocolEntryPermission(),
      isEditable: this.userHasUpdateProtocolEntryPermission(),
      name: item.label,
      showLine: i === 1 && arr.length !== 1,
      tooltip: this.patientTrackerLockedTooltip(),
      value: item.value,
    }));
  }

  getOptionWithVisibility(option: ButtonToggleItem): ButtonToggleItem {
    return !option.show ? { ...option, show: new BehaviorSubject(true) } : option;
  }

  onCreateAmendment = () => {
    this.amendmentClick.emit(true);
  };

  get versionOptions(): VersionOption[] {
    return this._versionOptions;
  }

  @Input() set versionOptions(value: VersionOption[]) {
    this._versionOptions = value;

    this.enhancedVersionList.set(this.convertOptionToEditableState(value));
  }
}
